import { useState, useEffect } from "react";
import { FileText, Briefcase, DollarSign, ShoppingBag, RotateCcw, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

interface ChallengeTemplate {
  id: string;
  name: string;
  description: string;
  type: string;
  default_reward: string | null;
  default_deadline_days: number | null;
  default_tags: string[] | null;
  template_content: string | null;
}

interface ChallengeTemplatesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectTemplate: (template: ChallengeTemplate) => void;
  hasDraft?: boolean;
  onRestoreDraft?: () => void;
  onDiscardDraft?: () => void;
}

const categoryConfig = {
  hire: {
    label: "Hiring Challenges",
    description: "Find talent through proven work",
    icon: Briefcase,
    colorClass: "text-blue-500 bg-blue-500/10 border-blue-500/20",
    badgeClass: "text-blue-500 bg-blue-500/10",
  },
  solve: {
    label: "Bounties",
    description: "Pay for solutions to problems",
    icon: DollarSign,
    colorClass: "text-green-500 bg-green-500/10 border-green-500/20",
    badgeClass: "text-green-500 bg-green-500/10",
  },
  sell: {
    label: "Commissions",
    description: "Reward successful sales & deals",
    icon: ShoppingBag,
    colorClass: "text-purple-500 bg-purple-500/10 border-purple-500/20",
    badgeClass: "text-purple-500 bg-purple-500/10",
  },
};

export const ChallengeTemplatesModal = ({
  open,
  onOpenChange,
  onSelectTemplate,
  hasDraft,
  onRestoreDraft,
  onDiscardDraft,
}: ChallengeTemplatesModalProps) => {
  const [templates, setTemplates] = useState<ChallengeTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const { data, error } = await supabase
          .from("challenge_templates")
          .select("*")
          .eq("is_public", true)
          .order("name");

        if (error) throw error;
        setTemplates(data as ChallengeTemplate[]);
      } catch (error) {
        console.error("Failed to fetch templates:", error);
        setTemplates([]);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchTemplates();
      setSelectedCategory(null);
    }
  }, [open]);

  const groupedTemplates = templates.reduce((acc, template) => {
    const type = template.type as keyof typeof categoryConfig;
    if (!acc[type]) acc[type] = [];
    acc[type].push(template);
    return acc;
  }, {} as Record<string, ChallengeTemplate[]>);

  const renderCategorySelection = () => (
    <div className="grid gap-3 sm:gap-4 py-2 sm:py-4">
      {/* Continue with draft option */}
      {hasDraft && onRestoreDraft && (
        <div className="p-3 sm:p-4 bg-primary/5 border border-primary/20 rounded-lg">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-3">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-1.5 sm:p-2 rounded-lg bg-primary/10">
                <RotateCcw className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground text-sm sm:text-base">Continue where you left off</h3>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  You have an unsaved draft
                </p>
              </div>
            </div>
            <div className="flex gap-2 self-end sm:self-auto">
              {onDiscardDraft && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => onDiscardDraft()}
                  className="text-muted-foreground hover:text-destructive h-8"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
              <Button 
                size="sm"
                onClick={() => {
                  onRestoreDraft();
                  onOpenChange(false);
                }}
                className="h-8"
              >
                Restore Draft
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Blank template option */}
      <button
        onClick={() => {
          onSelectTemplate({
            id: "blank",
            name: "Blank Template",
            description: "Start from scratch",
            type: "solve",
            default_reward: null,
            default_deadline_days: 14,
            default_tags: null,
            template_content: null,
          });
          onOpenChange(false);
        }}
        className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-secondary/30 hover:bg-secondary/50 rounded-lg border border-dashed border-border transition-colors text-left"
      >
        <div className="p-2 sm:p-3 rounded-lg bg-secondary">
          <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-muted-foreground" />
        </div>
        <div>
          <h3 className="font-semibold text-foreground text-sm sm:text-base">Blank Template</h3>
          <p className="text-xs sm:text-sm text-muted-foreground">
            Start from scratch
          </p>
        </div>
      </button>

      {/* Category cards */}
      <div className="grid gap-2 sm:gap-3 mt-1 sm:mt-2">
        <p className="text-xs sm:text-sm font-medium text-muted-foreground">Or choose a category:</p>
        {(Object.keys(categoryConfig) as Array<keyof typeof categoryConfig>).map((type) => {
          const config = categoryConfig[type];
          const Icon = config.icon;
          const templateCount = groupedTemplates[type]?.length || 0;

          return (
            <button
              key={type}
              onClick={() => setSelectedCategory(type)}
              className={cn(
                "flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg border transition-colors text-left",
                "bg-card hover:bg-secondary/30 border-border hover:border-primary/30"
              )}
            >
              <div className={cn("p-2 sm:p-3 rounded-lg", config.colorClass)}>
                <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-semibold text-foreground text-sm sm:text-base">{config.label}</h3>
                  <span className="text-xs text-muted-foreground shrink-0">
                    {templateCount} template{templateCount !== 1 ? 's' : ''}
                  </span>
                </div>
                <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 sm:mt-1">
                  {config.description}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderTemplatesList = () => {
    const config = categoryConfig[selectedCategory as keyof typeof categoryConfig];
    const Icon = config?.icon || FileText;
    const categoryTemplates = groupedTemplates[selectedCategory!] || [];

    return (
      <div className="space-y-3 sm:space-y-4 py-2 sm:py-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setSelectedCategory(null)}
          className="text-muted-foreground hover:text-foreground -ml-2 h-8 text-sm"
        >
          ← Back to categories
        </Button>

        <div className="flex items-center gap-2 sm:gap-3 pb-2 border-b border-border">
          <div className={cn("p-1.5 sm:p-2 rounded-lg", config?.colorClass)}>
            <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground text-sm sm:text-base">{config?.label}</h3>
            <p className="text-xs text-muted-foreground">{config?.description}</p>
          </div>
        </div>

        <div className="grid gap-2 sm:gap-3">
          {categoryTemplates.length === 0 ? (
            <p className="text-xs sm:text-sm text-muted-foreground py-4 text-center">
              No templates available in this category yet.
            </p>
          ) : (
            categoryTemplates.map((template) => (
              <button
                key={template.id}
                onClick={() => {
                  onSelectTemplate(template);
                  onOpenChange(false);
                }}
                className="flex flex-col gap-1.5 sm:gap-2 p-3 sm:p-4 bg-card hover:bg-secondary/30 rounded-lg border border-border transition-colors text-left"
              >
                <div className="flex items-center justify-between gap-2">
                  <h4 className="font-medium text-foreground text-sm sm:text-base">{template.name}</h4>
                  {template.default_reward && (
                    <span className={cn("text-xs px-2 py-0.5 rounded-full font-medium shrink-0", config?.badgeClass)}>
                      {template.default_reward}
                    </span>
                  )}
                </div>
                <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2">
                  {template.description}
                </p>
                {template.default_tags && template.default_tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-0.5 sm:mt-1">
                    {template.default_tags.slice(0, 4).map((tag) => (
                      <span key={tag} className="text-xs px-1.5 sm:px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </button>
            ))
          )}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[80vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader className="pb-2">
          <DialogTitle className="text-base sm:text-lg">
            {selectedCategory ? categoryConfig[selectedCategory as keyof typeof categoryConfig]?.label : "Choose a Template"}
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm">
            {selectedCategory 
              ? "Select a template to get started"
              : "Start with a template or from scratch"
            }
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="grid gap-3 py-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 sm:h-24 bg-secondary rounded-lg animate-pulse" />
            ))}
          </div>
        ) : selectedCategory ? (
          renderTemplatesList()
        ) : (
          renderCategorySelection()
        )}
      </DialogContent>
    </Dialog>
  );
};
